/* pz1-fixes.js - product 1 runtime fixes (15.9.2026)
 *
 * Companion to assets/pz1-fixes.css. Loaded on product pages only, from
 * snippets/stylesheets.liquid. Rolling back = deleting that one line.
 * Every block below is independently revertible - just delete the block.
 *
 * CORRECTION (15.9, later the same day): the header used to say that
 * assets/custom-script.js is minified - "99 lines, the code inside a single
 * 181,329-character line" - and therefore uneditable. That was wrong, and
 * the reason is worth remembering: SHOPIFY MINIFIES JS ASSETS ON DELIVERY.
 * The Admin API returns the SOURCE (4,100 normal lines; only the FONT_DATA
 * glyph table on line 3397 is huge), while the browser receives a minified
 * build. The original measurement was taken on the delivered file. So the
 * engine IS editable in the code editor, and findings B and C1 have since
 * been fixed at source. See deploy/product1-round2-paste.md.
 *
 * Full reasoning: architecture/product1-qa-findings.md and
 * architecture/product1-rotation-and-text-drag.md
 */
(function () {
  'use strict';

  /* FINDING C1 + C3 - a hardcoded yellow wash behind the pillow, and the
   * iPad/desktop mismatch.
   *
   * NOW ALSO FIXED AT SOURCE (custom-script.js:2804 -> gl.clearColor(0,0,0,0)).
   * This block is kept as a backstop: it costs nothing, it is a no-op while
   * the source fix is in place, and it protects us if that edit is ever
   * reverted. The explanation below is left intact because it is the record
   * of why the value was wrong.
   *
   * custom-script.js, drawScene():
   *     const isIphoneV = /iPhone/i.test(navigator.userAgent);
   *     if (isIphoneV) { gl.clearColor(<editor colour>) }
   *     else           { gl.clearColor(1.0, 0.8706, 0.3529, 0.25) }
   *
   * 1.0, 0.8706, 0.3529 is rgb(255, 222, 90) - the brand yellow - at 25%
   * alpha. So on desktop and Android (and iPad, because the CSS checks
   * /iPad|iPhone|iPod/ while this checks /iPhone/i only) the canvas is
   * cleared to a yellow wash that NO theme setting can change.
   *
   * Measured on the page: 6 of 6 clearColor calls used exactly these values.
   *
   * The fix restores what the original author clearly intended - the line
   * right above it is commented out in the source and reads:
   *     // Clear with zero alpha to let the page background show through
   * So we substitute fully transparent. The canvas then shows whatever is
   * behind it, which is .c-wrapper - and THAT is controlled from the theme
   * editor via pillow_canvas_bg_type / pillow_canvas_bg_color_1 / _2.
   * Net effect: the wash is gone, the background becomes editor-controlled,
   * and iPad matches desktop matches iPhone.
   *
   * The guard matches the colour AND the alpha, so every other clearColor
   * call passes through untouched - including the iPhone branch, which uses
   * the editor colour with iphone_bg_alpha. Matching alpha too matters: if
   * someone ever sets iphone_bg_color to the brand yellow #FFDE5A, that is
   * the same RGB as the hardcoded value, and without the alpha check we
   * would silently neutralise a colour the merchant actually chose. The
   * hardcoded call is always alpha 0.25; iphone_bg_alpha is currently 100.
   */
  var TARGET = [1.0, 0.8706, 0.3529, 0.25];
  var EPS = 0.01;

  function near(a, b) { return Math.abs(a - b) < EPS; }

  function patch(proto) {
    if (!proto || !proto.clearColor || proto.__pz1Patched) return;
    var original = proto.clearColor;
    proto.clearColor = function (r, g, b, a) {
      if (near(r, TARGET[0]) && near(g, TARGET[1]) &&
          near(b, TARGET[2]) && near(a, TARGET[3])) {
        return original.call(this, 0, 0, 0, 0);
      }
      return original.apply(this, arguments);
    };
    proto.__pz1Patched = true;
  }

  if (window.WebGLRenderingContext) patch(window.WebGLRenderingContext.prototype);
  if (window.WebGL2RenderingContext) patch(window.WebGL2RenderingContext.prototype);


  /* FINDING E1 - the timer showed an impossible clock time.
   *
   * pillow-engine.js:
   *     function formatTimer(seconds) {
   *       const sec = Math.floor(seconds);
   *       const microsec = Math.floor((seconds - sec) * 100);
   *       return `${sec}:${microsec.toString().padStart(2, '0')}`;
   *     }
   *
   * That is seconds:hundredths, but a colon reads as mm:ss - so customers
   * saw "10:92" and "6:77", times that cannot exist. Observed live, both in
   * the live counter (#timerDisplay) and in the completion message
   * (#statusText, "Complete! Total time: 7:11").
   *
   * formatTimer is private to pillow-engine.js so it cannot be replaced from
   * here, and that file is not safely re-transmittable (it differs from the
   * local export and is full of Hebrew strings). Its only output is the text
   * of those two elements, so we reformat the text instead: 10:92 -> 10.9.
   *
   * No feedback loop is possible: the output does not match the input
   * pattern, so rewriting never triggers another rewrite.
   */
  var SECONDS_WORD = 'שניות';
  var HAS_TIMER = /\d+:\d{2}(?!\d)/;
  var ALL_TIMERS = /(\d+):(\d{2})(?!\d)/g;

  function prettify(text) {
    return text.replace(ALL_TIMERS, function (whole, secs, hundredths) {
      return secs + '.' + hundredths.charAt(0) + ' ' + SECONDS_WORD;
    });
  }

  function watchTimerText(id) {
    var el = document.getElementById(id);
    if (!el) return false;

    var apply = function () {
      var text = el.textContent;
      if (!HAS_TIMER.test(text)) return;
      var fixed = prettify(text);
      if (fixed !== text) el.textContent = fixed;
    };

    new MutationObserver(apply).observe(el, {
      childList: true,
      characterData: true,
      subtree: true
    });
    apply();
    return true;
  }

  function wireTimers() {
    watchTimerText('timerDisplay');
    watchTimerText('statusText');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wireTimers);
  } else {
    wireTimers();
  }


  /* FINDING C4 - the "pillow size" slider in the theme editor did nothing.
   *
   * When pillow_scale is not 100 the section emits
   *     .c-wrapper #glcanvas { transform: scale(<n>); transform-origin: center }
   * but the section ALSO emits
   *     #glcanvas[style] { ... transform: none !important; ... }
   * Both selectors are specificity (1,1,0), the second is declared later, so
   * it wins and the scale is silently discarded.
   *
   * Measured: injecting exactly the rule the section generates left the
   * computed transform at "none".
   *
   * A static CSS file cannot fix this - it has no access to the section
   * setting, so it cannot know what the scale should be. But the value IS in
   * the page: the section's own rule is in the stylesheets. So we read it back
   * and re-apply it as an inline style with priority, which beats any
   * stylesheet !important.
   *
   * Safe by construction: if pillow_scale is 100 the section emits no
   * transform rule, we find nothing, and this does nothing at all.
   *
   * A visual scale does not change the layout box, so clientWidth /
   * clientHeight stay equal and the FINDING B fix is unaffected.
   */
  function reviveTransform() {
    var canvas = document.getElementById('glcanvas');
    if (!canvas) return;

    var wanted = null;
    var origin = null;
    var sheets = document.styleSheets;

    for (var i = 0; i < sheets.length; i++) {
      var rules;
      try { rules = sheets[i].cssRules; } catch (e) { continue; }
      if (!rules) continue;
      for (var j = 0; j < rules.length; j++) {
        var rule = rules[j];
        if (!rule.selectorText || !rule.style) continue;
        if (rule.selectorText.indexOf('#glcanvas') === -1) continue;
        if (rule.selectorText.indexOf('[style]') !== -1) continue;
        var t = rule.style.transform || rule.style.webkitTransform;
        if (t && t !== 'none') {
          wanted = t;
          origin = rule.style.transformOrigin || 'center center';
        }
      }
    }

    if (!wanted) return;
    canvas.style.setProperty('transform', wanted, 'important');
    canvas.style.setProperty('transform-origin', origin, 'important');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reviveTransform);
  } else {
    reviveTransform();
  }


  /* ===== shared helpers for the two blocks below ===== */

  /* The step signal. goToStep (section line 6893) does
   *     document.body.classList.toggle('pz-on-text', step === 3)
   * with no mobile condition, and the step panels are shared between the two
   * layouts (the "mobile-step" class name is a misnomer - verified in the
   * live DOM). currentStep itself sits in a different IIFE and is not
   * reachable from outside, so this class is the signal.
   */
  function onTextStep() {
    return document.body.classList.contains('pz-on-text');
  }

  /* rotationEnabled is a top-level `let` in custom-script.js. It is not a
   * window property, but it IS in the global lexical scope, so new Function
   * can see it - the same bridge the section itself uses (line 5874).
   */
  var readSpin = (function () {
    try {
      return new Function(
        'try { return typeof rotationEnabled === "boolean" ? rotationEnabled : false; }' +
        ' catch (e) { return false; }');
    } catch (e) {
      return function () { return false; };
    }
  }());


  /* FINDING D (Elad, 15.9) - the pillow resumed spinning the instant the
   * finger lifted, so it walked away from the angle the customer chose.
   *
   * custom-script.js:4080
   *     if (lastTime !== 0 && !isDragging && rotationEnabled) { rotY -= ... }
   * and handleEnd() (line 3367) only clears isDragging - it never touches
   * rotationEnabled - while the rAF loop self-schedules every frame
   * (line 4100). So on the very next frame after mouseup/touchend the
   * rotation was back.
   *
   * Product 2 behaves the way Elad wants: app.js:2198 turns the spin off on
   * pointerdown and leaves it off; it comes back only on a step transition.
   *
   * Why we click the button instead of writing rotationEnabled = false:
   * the click handler lives in pillow-engine.js:66-73 and it ALSO swaps the
   * button label between "stop" and "play". Writing the flag directly would
   * leave the label lying, and the next real click would invert its meaning.
   * The section uses exactly this trick in pzFaceFront() (line 5878).
   *
   * Why document + capture: a listener added later on #glcanvas would run
   * after the engine's own mousedown/touchstart. The capture phase on
   * document runs before the target phase, so registration order stops
   * mattering.
   *
   * Step 3 is excluded: there the pillow must not rotate at all, and the
   * section already swallows the event before the engine sees it.
   *
   * passive: true is deliberate - we never call preventDefault here, so the
   * browser keeps its scrolling fast path.
   */
  function stopSpinOnGrab(e) {
    var t = e.target;
    if (!t || t.id !== 'glcanvas') return;
    if (onTextStep()) return;
    if (!readSpin()) return;
    var btn = document.getElementById('toggleRotationBtn');
    if (btn) btn.click();
  }

  document.addEventListener('mousedown', stopSpinOnGrab, true);
  document.addEventListener('touchstart', stopSpinOnGrab, { capture: true, passive: true });


  /* FINDING F (Elad, 15.9) - there was no pinch anywhere in product 1.
   *
   * The section collapses every touch to one finger (pointOf, line 5926:
   * "if (e.touches && e.touches.length) return e.touches[0]"), and it has no
   * wheel or gesture handler at all. Text size could only be changed with
   * the #pzTextSize slider.
   *
   * Rather than write our own sizing code, we DRIVE THAT SLIDER, because the
   * section already listens to it (lines 5823-5831):
   *     input  -> S.sizeRatio = value / 1000; render(); checkFit();
   *     change -> commit()   (ensureValid + syncControls + render +
   *                           updateTextProperty + updateDragLayer + checkFit)
   * So the pinch rides the section's own tested path: the range input
   * enforces its own 35..300 bounds, the slider stays visually in sync, and
   * the "text is outside the print area" warning keeps working. No new
   * rendering code, no duplicated clamping logic.
   *
   * ORDERING - this took a measured debugging round, so the facts are here.
   *
   * The section's whole text block, listener registration included (line
   * 6015), lives inside pzTextBoot(), which it runs on DOMContentLoaded
   * (line 6307). Deferred scripts execute BEFORE DOMContentLoaded, so THIS
   * FILE REGISTERS FIRST and the section registers second - the opposite of
   * what you would assume from reading the page top to bottom.
   *
   * Two consequences, both handled:
   *
   * 1. On a two-finger touchstart our onStart runs before the section's
   *    onDragStart, so stopImmediatePropagation() keeps the section from
   *    starting a text drag at all. (stopPropagation alone would not: it
   *    does not affect other listeners on the same node.) The synthetic
   *    mouseup is still dispatched, because on a real phone finger 1 lands
   *    first - one touch, we return early, the section starts a drag - and
   *    only then does finger 2 arrive. That mouseup ends the section's drag
   *    through its own onDragEnd (document, line 6021: `drag = null;
   *    commit();`) and also clears the engine's isDragging via handleEnd.
   *
   * 2. onMove is on DOCUMENT, not on #mainImageWrapper. Capture runs
   *    window -> document -> ... -> target, and the section's onDragMove
   *    sits on document with capture; when it is dragging it calls
   *    stopPropagation, which would hide the event from any listener further
   *    down the tree. MEASURED: with onMove on #mainImageWrapper the
   *    touchmove reached document and never reached the frame, so the pinch
   *    silently did nothing. On the same node stopPropagation cannot hide
   *    the event from us, and being registered first we also get to call
   *    stopImmediatePropagation and own the gesture outright.
   *
   * Consequence, and it matches product 2 on purpose (app.js:2301-2311):
   * when one finger lifts after a pinch, the remaining finger does NOT
   * resume dragging. The customer lifts and touches again.
   *
   * Only while body.pz-on-text is set (step 3) and only with exactly two
   * touches. One finger is left entirely to the section.
   */
  function pinchDist(t) {
    var dx = t[0].clientX - t[1].clientX;
    var dy = t[0].clientY - t[1].clientY;
    return Math.max(0.0001, Math.sqrt(dx * dx + dy * dy));
  }

  function wirePinch() {
    var frame = document.getElementById('mainImageWrapper');
    var slider = document.getElementById('pzTextSize');
    if (!frame || !slider) return false;

    var lo = Number(slider.min) || 35;
    var hi = Number(slider.max) || 300;
    var pinch = null;

    var onStart = function (e) {
      if (!onTextStep()) return;
      if (!e.touches || e.touches.length !== 2) return;

      pinch = { d0: pinchDist(e.touches), v0: Number(slider.value) || 85 };

      /* stop the section from starting a text drag on this touch... */
      e.stopPropagation();
      e.stopImmediatePropagation();
      if (e.cancelable) e.preventDefault();

      /* ...and end one it may already have started with the first finger,
         through its own onDragEnd rather than by reaching into its state. */
      document.dispatchEvent(new MouseEvent('mouseup', { bubbles: true }));
    };

    var onMove = function (e) {
      if (!pinch) return;
      if (!e.touches || e.touches.length < 2) return;

      e.stopPropagation();
      e.stopImmediatePropagation();
      if (e.cancelable) e.preventDefault();

      var v = Math.round(pinch.v0 * pinchDist(e.touches) / pinch.d0);
      v = Math.min(hi, Math.max(lo, v));
      if (String(v) === String(slider.value)) return;

      slider.value = String(v);
      slider.dispatchEvent(new Event('input', { bubbles: true }));
    };

    var onEnd = function (e) {
      if (!pinch) return;
      if (e.touches && e.touches.length >= 2) return;
      pinch = null;
      slider.dispatchEvent(new Event('change', { bubbles: true }));
    };

    frame.addEventListener('touchstart', onStart, { capture: true, passive: false });
    document.addEventListener('touchmove', onMove, { capture: true, passive: false });
    document.addEventListener('touchend', onEnd, true);
    document.addEventListener('touchcancel', onEnd, true);
    return true;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wirePinch);
  } else {
    wirePinch();
  }
}());
